import { AlertTriangle, ArrowDownToLine, Banknote, CheckCircle2, Clock, Loader2, Mail, RefreshCw, Search, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminListDeposits, adminReplayCryptoDeposit, adminReplayDeposit } from '@/api/admin';
import AdminDateRange, { dateRangeParams, EMPTY_DATE_RANGE, useDebouncedValue } from '@/components/admin/AdminDateRange';
import AdminPagination from '@/components/admin/AdminPagination';
import { fmtUsd } from '@/lib/money';

/**
 * DepositsSubpage — Admin oversight of NOWPayments deposits.
 *
 * What it shows
 * -------------
 *   - Paginated table of every nowpayments_payments doc (25 per page).
 *   - Status filter chips: All / Pending / Completed / Voided.
 *   - Search box matching username (prefix, case-insensitive) OR exact
 *     payment_id.
 *
 * Replay flow
 * -----------
 *   - Inline "Replay" button on every row.
 *   - Top-level "Replay by Payment ID" form for the rare case where the
 *     admin has a payment_id NOT yet in the local DB (or wants to re-check
 *     one by hand without first finding it in the table).
 *   - Backend POST /api/admin/deposits/{payment_id}/replay performs a live
 *     NOWPayments status check, and if the deposit is "finished" but our
 *     row isn't credited yet, it dispatches the SAME IPN handler the real
 *     IPN webhook uses — fully idempotent (cannot double-credit).
 */

const STATUS_FILTERS = [
  { id: '', label: 'All', icon: Banknote },
  { id: 'pending', label: 'Pending', icon: Clock },
  { id: 'completed', label: 'Completed', icon: CheckCircle2 },
  { id: 'voided', label: 'Voided', icon: XCircle },
];

const statusStyle = (s) => {
  if (s === 'completed')
    return {
      bg: 'rgba(56,189,127,0.14)',
      border: 'rgba(56,189,127,0.50)',
      color: '#86efac',
    };
  if (s === 'voided')
    return {
      bg: 'rgba(160,160,160,0.10)',
      border: 'rgba(160,160,160,0.32)',
      color: 'var(--sc-text-mute)',
    };
  return {
    bg: 'rgba(255,194,14,0.10)',
    border: 'rgba(255,194,14,0.45)',
    color: '#FFE08A',
  };
};

const shortId = (s, len = 10) => (!s ? '—' : s.length <= len ? s : `${s.slice(0, len)}…`);

/**
 * Normalize the v2 (crypto-first) replay response into the v1-shaped status
 * union {credited, already_credited, not_finished_yet, unknown_payment_id,
 * noop} so the existing toast-mapping in handleReplay works uniformly.
 *
 * v2 payload shape:
 *   { replay_result: { credited: bool, reason: str, amount_cents: int },
 *     fresh_np_payload: { payment_status, ... } }
 */
const _normalizeV2Replay = (raw) => {
  const rr = raw?.replay_result || {};
  const fresh = raw?.fresh_np_payload || {};
  const reason = rr.reason || '';
  const cents = Number(rr.amount_cents || 0);
  const usd = cents > 0 ? (cents / 100).toFixed(2) : '';
  if (rr.credited === true) {
    return { status: 'credited', credited_amount_usd: usd, np_status: fresh.payment_status, reason };
  }
  if (reason === 'already_credited' || reason === 'cas_lost_already_credited') {
    return { status: 'already_credited', credited_amount_usd: usd, np_status: fresh.payment_status, reason };
  }
  if (reason.startsWith('non_terminal_status')) {
    return { status: 'not_finished_yet', np_status: fresh.payment_status, reason };
  }
  if (reason === 'deposit_not_found') {
    return { status: 'unknown_payment_id', np_status: fresh.payment_status, reason };
  }
  return { status: 'noop', np_status: fresh.payment_status, reason };
};

// Source badge styling (Patch v13). v2 = gold (the new flow); v1 = neutral.
const sourceStyle = (source) => {
  if (source === 'crypto_first')
    return {
      bg: 'rgba(255,194,14,0.14)',
      border: 'rgba(255,194,14,0.55)',
      color: '#FFE08A',
      label: 'Crypto',
    };
  return {
    bg: 'rgba(160,160,160,0.10)',
    border: 'rgba(160,160,160,0.32)',
    color: 'var(--sc-text-mute)',
    label: 'Legacy',
  };
};

const DepositsSubpage = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [dates, setDates] = useState(EMPTY_DATE_RANGE);
  const appliedDates = useDebouncedValue(dates);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  // Inline replay state (keyed by payment_id so multiple rows can run
  // in parallel without trampling each other's spinners).
  const [replayingId, setReplayingId] = useState(null);
  const [replayResults, setReplayResults] = useState({});
  // Standalone "Replay by payment ID" input — for forensic recovery when
  // the deposit may not be in the local list.
  const [manualPaymentId, setManualPaymentId] = useState('');
  const [manualReplaying, setManualReplaying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminListDeposits({
        page,
        pageSize,
        status,
        q: q.trim(),
        dates: dateRangeParams(appliedDates),
      });
      setData(r);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Failed to load deposits: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status, q, appliedDates]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-loading whenever the user types into search; 300ms debounce so we
  // don't hammer the backend on every keystroke.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the debounced reload must fire only on search-text changes; page/status changes are already loaded immediately by the effect above
  useEffect(() => {
    const t = setTimeout(load, 280);
    return () => clearTimeout(t);
  }, [q]);

  const handleReplay = async (paymentId, source = 'sub_partner') => {
    if (!paymentId || replayingId) return;
    setReplayingId(paymentId);
    try {
      // Route to the matching backend endpoint based on the deposit's source.
      // v1 (sub_partner) → existing /api/admin/deposits/{id}/replay
      // v2 (crypto_first) → /api/admin/payments/crypto/replay/{id}
      // Both responses are normalized to the v1-shaped {status, ...} union
      // so the toast-mapping below stays unchanged.
      let r;
      if (source === 'crypto_first') {
        const raw = await adminReplayCryptoDeposit(paymentId);
        r = _normalizeV2Replay(raw);
      } else {
        r = await adminReplayDeposit(paymentId);
      }
      setReplayResults((prev) => ({ ...prev, [paymentId]: r }));
      const map = {
        credited: {
          fn: toast.success,
          msg: `Credited ${r?.credited_amount_usd ? fmtUsd(r.credited_amount_usd) : ''} for payment ${shortId(paymentId)} — user balance updated.`,
        },
        already_credited: {
          fn: toast.info,
          msg: `Payment ${shortId(paymentId)} was already credited. No double-credit possible.`,
        },
        not_finished_yet: {
          fn: toast.warning,
          msg: `Payment ${shortId(paymentId)} is not finished on NOWPayments yet (status: ${r?.np_status || 'unknown'}).`,
        },
        unknown_payment_id: {
          fn: toast.error,
          msg: `Payment ${shortId(paymentId)} not found in this database.`,
        },
        noop: {
          fn: toast.info,
          msg: `Payment ${shortId(paymentId)} returned no-op (status: ${r?.np_status || '?'}). ${r?.reason || ''}`,
        },
      };
      const choice = map[r?.status] || map.noop;
      choice.fn(choice.msg);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Replay failed: ${msg}`);
    } finally {
      setReplayingId(null);
    }
  };

  const handleManualReplay = async () => {
    if (!manualPaymentId.trim() || manualReplaying) return;
    const pid = manualPaymentId.trim();
    setManualReplaying(true);
    try {
      // Manual replay supports BOTH sources. Try v1 first; if v1 says the
      // payment is unknown, transparently retry against v2. This way the
      // operator only needs to paste a payment_id without thinking about
      // which flow it came from.
      let r = await adminReplayDeposit(pid).catch((err) => {
        const det = err?.response?.data?.detail;
        const code = typeof det === 'object' ? det?.code : det;
        if (err?.response?.status === 404 || code === 'UNKNOWN_PAYMENT_ID') {
          return { status: 'unknown_payment_id' };
        }
        throw err;
      });
      if (r?.status === 'unknown_payment_id') {
        const v2raw = await adminReplayCryptoDeposit(pid).catch((err) => {
          const _det = err?.response?.data?.detail;
          if (err?.response?.status === 404) {
            return { _truly_unknown: true };
          }
          throw err;
        });
        if (v2raw?._truly_unknown) {
          toast.error(`Payment ${shortId(pid)} not found in either deposits collection.`);
          return;
        }
        r = _normalizeV2Replay(v2raw);
      }
      setReplayResults((prev) => ({ ...prev, [pid]: r }));
      const map = {
        credited: {
          fn: toast.success,
          msg: `Credited ${r?.credited_amount_usd ? fmtUsd(r.credited_amount_usd) : ''} for payment ${shortId(pid)} — user balance updated.`,
        },
        already_credited: {
          fn: toast.info,
          msg: `Payment ${shortId(pid)} was already credited. No double-credit possible.`,
        },
        not_finished_yet: {
          fn: toast.warning,
          msg: `Payment ${shortId(pid)} is not finished on NOWPayments yet (status: ${r?.np_status || 'unknown'}).`,
        },
        unknown_payment_id: {
          fn: toast.error,
          msg: `Payment ${shortId(pid)} not found in this database.`,
        },
        noop: {
          fn: toast.info,
          msg: `Payment ${shortId(pid)} returned no-op (status: ${r?.np_status || '?'}). ${r?.reason || ''}`,
        },
      };
      const choice = map[r?.status] || map.noop;
      choice.fn(choice.msg);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Replay failed: ${msg}`);
    } finally {
      setManualReplaying(false);
      setManualPaymentId('');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-deposits-subpage">
      {/* Header + manual replay */}
      <div className="sc-glass rounded-2xl p-5 md:p-6">
        <div className="flex items-center gap-2 text-[#FFC20E]">
          <Banknote className="w-4 h-4" />
          <div className="font-display font-semibold text-sm uppercase tracking-[0.18em]">Deposits Oversight</div>
        </div>
        <h3 className="font-display text-2xl font-bold mt-2">NOWPayments Deposits</h3>
        <p className="text-[12px] text-[var(--sc-text-dim)] mt-1.5 leading-relaxed max-w-3xl">
          Live view of every deposit invoice in the platform. Use <strong>Replay</strong> when a deposit shows <em>finished</em> on the NOWPayments dashboard
          but the user's Purchase Balance never updated — the replay re-runs the IPN credit path safely (idempotent, cannot double-credit).
        </p>

        {/* Manual payment-id replay */}
        <div
          className="mt-5 rounded-xl border p-4"
          style={{
            background: 'rgba(255,182,18,0.04)',
            borderColor: 'rgba(255,182,18,0.24)',
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-2">Replay by NOWPayments Payment ID</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              className="sc-input flex-1"
              value={manualPaymentId}
              onChange={(e) => setManualPaymentId(e.target.value)}
              placeholder="e.g. 5037253699"
              data-testid="admin-deposit-manual-replay-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleManualReplay();
              }}
              autoComplete="off"
              spellCheck="false"
            />
            <button
              type="button"
              onClick={handleManualReplay}
              disabled={!manualPaymentId.trim() || manualReplaying}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-display font-bold text-sm transition disabled:opacity-60 shrink-0"
              style={{
                background: 'linear-gradient(135deg, #FFC20E, #FFB612)',
                color: '#0a0a0b',
                border: '1px solid rgba(255,182,18,0.85)',
                cursor: manualPaymentId.trim() ? 'pointer' : 'not-allowed',
              }}
              data-testid="admin-deposit-manual-replay-btn"
            >
              {manualReplaying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Replay
            </button>
          </div>
        </div>
      </div>

      {/* Filters + search */}
      <div className="sc-glass rounded-2xl p-4 md:p-5">
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => {
              const Icon = f.icon;
              const active = f.id === status;
              return (
                <button
                  key={f.id || 'all'}
                  type="button"
                  onClick={() => {
                    setStatus(f.id);
                    setPage(1);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-[0.18em] font-bold border transition"
                  style={{
                    background: active ? 'rgba(255,194,14,0.18)' : 'rgba(0,0,0,0.32)',
                    borderColor: active ? 'rgba(255,194,14,0.65)' : 'rgba(255,182,18,0.20)',
                    color: active ? '#FFE08A' : 'var(--sc-text-mute)',
                  }}
                  data-testid={`admin-deposits-filter-${f.id || 'all'}`}
                >
                  <Icon className="w-3 h-3" />
                  {f.label}
                </button>
              );
            })}
          </div>
          <div className="flex-1 relative md:ml-auto">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--sc-text-mute)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              className="sc-input pl-9 w-full md:max-w-[360px]"
              placeholder="Search by username or payment_id"
              data-testid="admin-deposits-search"
              autoComplete="off"
              spellCheck="false"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <AdminDateRange
            wrap
            dateFrom={dates.date_from}
            dateTo={dates.date_to}
            onChange={(range) => {
              setDates(range);
              setPage(1);
            }}
            fromLabel="Created From"
            toLabel="Created To"
            testIdPrefix="admin-deposits-date"
          />
        </div>
      </div>

      {/* Table */}
      <div className="sc-glass rounded-2xl p-4 md:p-5">
        {loading ? (
          <div className="flex items-center gap-3 text-[var(--sc-text-mute)] py-12 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading deposits…</span>
          </div>
        ) : data.items.length === 0 ? (
          <div className="py-12 text-center text-sm text-[var(--sc-text-dim)]">
            <Banknote className="w-6 h-6 mx-auto text-[var(--sc-text-mute)] mb-2" />
            No deposits match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="admin-deposits-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] border-b border-[rgba(255,182,18,0.16)]">
                  <th className="py-2 px-3">Member</th>
                  <th className="py-2 px-3">Payment ID</th>
                  <th className="py-2 px-3">Source</th>
                  <th className="py-2 px-3">Asset</th>
                  <th className="py-2 px-3 text-right">Amount</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3">Created</th>
                  <th className="py-2 px-3 text-right">Replay</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => {
                  const st = statusStyle(row.status);
                  const lastResult = replayResults[row.payment_id];
                  return (
                    <tr
                      key={row.payment_id}
                      className="border-b border-[rgba(255,182,18,0.06)] hover:bg-[rgba(255,194,14,0.03)] transition"
                      data-testid={`admin-deposit-row-${row.payment_id}`}
                    >
                      <td className="py-3 px-3">
                        <div className="font-semibold">{row.username || <span className="text-[var(--sc-text-mute)] italic">(orphan)</span>}</div>
                        {row.email ? (
                          <a
                            href={`mailto:${row.email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 mt-0.5 text-[10px] text-[var(--sc-text-dim)] hover:text-[#FFC20E] transition break-all"
                          >
                            <Mail className="w-2.5 h-2.5" />
                            {row.email}
                          </a>
                        ) : null}
                      </td>
                      <td className="py-3 px-3 font-mono text-[11px] text-[var(--sc-text-dim)]">{row.payment_id}</td>
                      <td className="py-3 px-3">
                        {(() => {
                          const ss = sourceStyle(row.source);
                          return (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[9.5px] uppercase tracking-[0.16em] font-bold"
                              style={{
                                background: ss.bg,
                                border: `1px solid ${ss.border}`,
                                color: ss.color,
                              }}
                              data-testid={`admin-deposit-source-${row.payment_id}`}
                              title={
                                row.source === 'crypto_first' ? 'Crypto-first single-use payment (v2 flow)' : 'Legacy sub-partner permanent address (v1 flow)'
                              }
                            >
                              {ss.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 px-3">
                        <span className="font-bold text-[#FFE08A]">{row.asset}</span>
                      </td>
                      <td className="py-3 px-3 text-right font-mono sc-tabular">{fmtUsd(row.amount_usd)}</td>
                      <td className="py-3 px-3">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-bold"
                          style={{
                            background: st.bg,
                            border: `1px solid ${st.border}`,
                            color: st.color,
                          }}
                          data-testid={`admin-deposit-status-${row.payment_id}`}
                        >
                          {row.status}
                        </span>
                        {row.np_status ? (
                          <div className="text-[9px] uppercase tracking-[0.14em] text-[var(--sc-text-mute)] mt-1">np: {row.np_status}</div>
                        ) : null}
                      </td>
                      <td className="py-3 px-3 text-[11px] text-[var(--sc-text-dim)]">{row.created_at?.slice(0, 19).replace('T', ' ') || '—'}</td>
                      <td className="py-3 px-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleReplay(row.payment_id, row.source)}
                          disabled={replayingId === row.payment_id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-[0.16em] transition disabled:opacity-60"
                          style={{
                            background: 'rgba(0,0,0,0.42)',
                            border: '1px solid rgba(255,182,18,0.45)',
                            color: '#FFE08A',
                          }}
                          data-testid={`admin-deposit-replay-${row.payment_id}`}
                          title={row.credited ? 'Re-verify (already credited — replay is a no-op)' : 'Re-run IPN credit path'}
                        >
                          {replayingId === row.payment_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : row.credited ? (
                            <CheckCircle2 className="w-3 h-3" />
                          ) : (
                            <RefreshCw className="w-3 h-3" />
                          )}
                          Replay
                        </button>
                        {lastResult ? <div className="text-[9px] mt-1 text-[var(--sc-text-mute)]">last: {lastResult.status}</div> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4">
          <AdminPagination
            page={data.page || page}
            totalPages={data.total_pages || Math.max(1, Math.ceil((data.total || 0) / pageSize))}
            total={data.total}
            pageSize={data.page_size || pageSize}
            onChange={setPage}
          />
        </div>
      </div>

      {/* Help footer */}
      <div
        className="rounded-xl p-4 text-[11px] text-[var(--sc-text-dim)] leading-relaxed flex gap-3"
        style={{
          background: 'rgba(255,182,18,0.04)',
          border: '1px solid rgba(255,182,18,0.18)',
        }}
        data-testid="admin-deposits-help"
      >
        <ArrowDownToLine className="w-3.5 h-3.5 mt-0.5 text-[#FFC20E] shrink-0" />
        <div>
          <strong className="text-[var(--sc-text)]">When to use Replay:</strong> if a user reports their deposit shows complete on NOWPayments but their
          Purchase Balance never updated. The replay endpoint fetches the live payment status from NOWPayments and, if the status is <em>finished</em>,
          dispatches the same handler the IPN webhook uses to credit the user. Already-credited deposits return a clear "already_credited" result and never
          double-credit. <AlertTriangle className="w-3 h-3 inline-block text-amber-400" /> Replay does NOT move on-chain funds — it only credits the internal
          Purchase Balance based on what NOWPayments has already confirmed.
        </div>
      </div>
    </div>
  );
};

export default DepositsSubpage;
